import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, BedDouble, Sparkles, MessageCircle } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";
import { UNITS } from "@/staticData";

const WaIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
    <path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35M12.04 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88M20.5 3.49A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.16-3.44-8.41" />
  </svg>
);

const GREETING = {
  ro: "Bună, Mihai! Aș dori să fac o rezervare la Imperfect House.",
  en: "Hi Mihai! I'd like to make a booking at Imperfect House.",
  it: "Ciao Mihai! Vorrei fare una prenotazione a Imperfect House.",
};

export default function WhatsAppFloat({ config }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wa = config?.whatsapp || "40721663181";
  const rooms = UNITS.filter((u) => u.type === "room" && u.active).sort((a, b) => a.sort_order - b.sort_order);

  const goRoom = (slug) => {
    setOpen(false);
    navigate(`/cazare/${slug}`);
  };

  const goQuiz = () => {
    setOpen(false);
    const go = () => scrollToId("#quiz");
    if (document.querySelector("#quiz")) {
      go();
      setTimeout(go, 900);
      setTimeout(go, 1800);
    } else {
      navigate("/#quiz");
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="w-72 bg-cream rounded-3xl shadow-2xl shadow-main/30 border border-warm-grey/40 overflow-hidden"
            data-testid="wa-menu"
          >
            <div className="bg-main text-cream px-5 py-4">
              <p className="font-serif text-lg" data-testid="wa-menu-title">{t("wa.menu_title")}</p>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-walnut px-1">{t("wa.menu_rooms")}</p>
              <div className="grid grid-cols-2 gap-2">
                {rooms.map((r) => (
                  <button key={r.id} onClick={() => goRoom(r.slug)} data-testid={`wa-menu-room-${r.id}`}
                    className="flex items-center gap-2 rounded-2xl border border-warm-grey/50 bg-cream-light px-3 py-2.5 text-sm font-semibold text-main hover:border-terracotta hover:text-terracotta transition-colors">
                    <BedDouble size={15} className="shrink-0" />
                    {(r[`name_${lang}`] || r.name_ro).replace("Camera ", "").replace("Room", "").trim()}
                  </button>
                ))}
              </div>
              <button onClick={goQuiz} data-testid="wa-menu-quiz"
                className="w-full flex items-center gap-2.5 rounded-2xl border border-warm-grey/50 bg-cream-light px-4 py-3 text-sm font-semibold text-main hover:border-terracotta hover:text-terracotta transition-colors">
                <Sparkles size={15} className="shrink-0" /> {t("wa.menu_quiz")}
              </button>
              <a href={waLink(wa, GREETING[lang] || GREETING.ro)} target="_blank" rel="noreferrer" data-testid="wa-menu-book"
                className="w-full flex items-center justify-center gap-2 rounded-2xl bg-green-dark text-cream-light px-4 py-3.5 text-sm font-bold hover:bg-terracotta transition-colors">
                <MessageCircle size={16} /> {t("wa.menu_book")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200 }}
        className="group relative"
        data-testid="whatsapp-float"
        aria-label={t("wa.tooltip")}
        aria-expanded={open}
      >
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-main text-cream text-xs font-semibold rounded-full px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          {t("wa.tooltip")}
        </span>
        <span className={`flex items-center justify-center w-14 h-14 rounded-full text-cream-light shadow-2xl shadow-green-dark/40 transition-colors duration-300 ${open ? "bg-terracotta" : "bg-green-dark animate-pulse-soft"}`}>
          {open ? <X size={22} /> : <WaIcon />}
        </span>
      </motion.button>
    </div>
  );
}
